import { Button } from "@mui/material";
import { useEffect, useMemo } from "react";
import { BackText, ContinueText, PleaseDescribeTheProblemReplacementText, RequiredFieldsText } from "constants/text";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { cartActions, ReplacementCartItem } from "features/reducers/replacementOrder/cart.ts";
import { useNavigate, useParams } from "react-router";
import { models } from "types/api/viewModels.ts";
import { useGetLinkedOrdersQuery } from "features/api/orderApi.ts";
import { OrderType } from "types/declarations/enums.ts";
import {
	ReplacementReasonButtonGroup,
	ReplacementReasonContainer,
	ReplacementReasonContentContainer,
	ReplacementReasonContentHeader,
	ReplacementReasonHeader
} from "components/Replacements/components/ReplacementReason/ReplacementReason.styles.ts";
import { ReplacementReasonBox } from "components/Replacements/ReplacementReasonBox/ReplacementReasonBox.tsx";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { array, object } from "yup";
import {
	FormSchema,
	schema as entrySchema
} from "components/Replacements/components/SelectReplacementReasonAndProblemDropdowns/SelectReplacementReasonAndProblemDropdowns.schema.ts";

export interface ReplacementReasonProp {
	orderDetail: models["OrderDetailViewModel"] | null | undefined;
}

type ItemFormValues = {
	cabinet: FormSchema | null;
	parts: FormSchema[];
};

export type ReplacementReasonFormValues = {
	items: ItemFormValues[];
};

const formSchema = object({
	items: array()
		.of(
			object({
				cabinet: entrySchema.nullable().optional(),
				parts: array().of(entrySchema).required()
			})
		)
		.required()
});

const buildDefaultValues = (
	sortedItems: ReplacementCartItem[],
	previouslyReplacedSkus: Set<string>
): ReplacementReasonFormValues => ({
	items: sortedItems.map((item) => ({
		cabinet:
			item.wholeCabinetQuantity > 0
				? {
						replacementReason: item.cabinetReplacementReason,
						replacementProblem: item.cabinetReplacementProblem,
						originalHasAnotherReplacementOrder: previouslyReplacedSkus.has(item.sku),
						qualityCheckFlag: item.qualityCheckFlag ?? null,
						fileUploads: item.cabinetFileUploads ?? [],
						comment: item.comment ?? null
					}
				: null,
		parts: item.replaceableParts
			.filter((p) => p.partQuantity > 0)
			.map((p) => ({
				replacementReason: p.partReplacementReason,
				replacementProblem: p.partReplacementProblem,
				originalHasAnotherReplacementOrder: previouslyReplacedSkus.has(p.partSku),
				qualityCheckFlag: p.qualityCheckFlag ?? null,
				fileUploads: p.partFileUploads ?? [],
				comment: p.comment ?? null
			}))
	}))
});

const ReplacementReason = ({ orderDetail }: ReplacementReasonProp) => {
	const navigateTo = useNavigate();
	const { orderId } = useParams();
	const { data: linkedOrders } = useGetLinkedOrdersQuery(orderId ?? "");
	const previouslyReplacedSkus = useMemo(() => {
		const skus = new Set<string>();
		linkedOrders?.forEach((order) => {
			if (order.orderType === OrderType.REPLACEMENT) {
				order.skUs?.forEach((sku) => {
					if (sku) {
						skus.add(sku);
					}
				});
			}
		});
		return skus;
	}, [linkedOrders]);

	const sortedItems: ReplacementCartItem[] = useSelector((state: RootState) =>
		[...state.cart.replacements].sort((a, b) => a.lineItemNumber - b.lineItemNumber)
	);

	const methods = useForm<ReplacementReasonFormValues>({
		mode: "onSubmit",
		reValidateMode: "onChange",
		resolver: yupResolver(formSchema) as any,
		defaultValues: buildDefaultValues(sortedItems, previouslyReplacedSkus)
	});

	// Keep originalHasAnotherReplacementOrder in sync per-item once the
	// linked-orders query resolves (it arrives after initial render). Only flag
	// items whose SKU appears in a prior REPLACEMENT linked order.
	useEffect(() => {
		sortedItems.forEach((item, itemIndex) => {
			if (item.wholeCabinetQuantity > 0) {
				methods.setValue(
					`items.${itemIndex}.cabinet.originalHasAnotherReplacementOrder` as any,
					previouslyReplacedSkus.has(item.sku)
				);
			}
			item.replaceableParts
				.filter((p) => p.partQuantity > 0)
				.forEach((part, partIndex) => {
					methods.setValue(
						`items.${itemIndex}.parts.${partIndex}.originalHasAnotherReplacementOrder` as any,
						previouslyReplacedSkus.has(part.partSku)
					);
				});
		});
	}, [previouslyReplacedSkus]); // eslint-disable-line react-hooks/exhaustive-deps

	const dispatch = useDispatch();

	const handleBack = () => {
		navigateTo(`/replacements/${orderDetail?.orderId}`);
	};

	const onValid = (data: ReplacementReasonFormValues) => {
		// Persist form values back to Redux so the form rehydrates correctly if the user navigates back
		sortedItems.forEach((item, itemIndex) => {
			const itemFormValues = data.items[itemIndex];
			let visiblePartIndex = 0;
			const updatedItem = {
				...item,
				cabinetReplacementReason: itemFormValues?.cabinet?.replacementReason ?? item.cabinetReplacementReason,
				cabinetReplacementProblem:
					itemFormValues?.cabinet?.replacementProblem ?? item.cabinetReplacementProblem,
				qualityCheckFlag: itemFormValues?.cabinet?.qualityCheckFlag ?? item.qualityCheckFlag,
				cabinetFileUploads: itemFormValues?.cabinet?.fileUploads ?? item.cabinetFileUploads ?? [],
				comment: itemFormValues?.cabinet?.comment ?? item.comment,
				replaceableParts: item.replaceableParts.map((part) => {
					if (part.partQuantity > 0) {
						const partFormValues = itemFormValues?.parts[visiblePartIndex++];
						return {
							...part,
							partReplacementReason: partFormValues?.replacementReason ?? part.partReplacementReason,
							partReplacementProblem: partFormValues?.replacementProblem ?? part.partReplacementProblem,
							qualityCheckFlag: partFormValues?.qualityCheckFlag ?? part.qualityCheckFlag,
							partFileUploads: partFormValues?.fileUploads ?? part.partFileUploads ?? [],
							comment: partFormValues?.comment ?? part.comment
						};
					}
					return part;
				})
			};
			dispatch(cartActions.updateItem(updatedItem));
		});
		navigateTo(`/replacements/shipping-details/${orderDetail?.orderId}`);
	};

	return (
		<FormProvider {...methods}>
			<form
				onSubmit={methods.handleSubmit(
					(data) => {
						onValid(data);
					},
					(errors) => {
						console.log("INVALID submit", errors);
					}
				)}
				noValidate
			>
				<ReplacementReasonContainer>
					<ReplacementReasonHeader data-testid="replacementReason-header">
						Tell us what happened
					</ReplacementReasonHeader>
					<ReplacementReasonContentContainer data-testid="replacementReason-tellUsWhyContainer">
						<ReplacementReasonContentHeader>
							<h2 data-testid="replacementReason-subheader">{PleaseDescribeTheProblemReplacementText}</h2>
							<span>{RequiredFieldsText}</span>
						</ReplacementReasonContentHeader>
						{sortedItems.map(
							(item, itemIndex) =>
								item && (
									<ReplacementReasonBox
										key={item.id}
										item={item}
										itemIndex={itemIndex}
									/>
								)
						)}
					</ReplacementReasonContentContainer>
					<ReplacementReasonButtonGroup>
						<Button
							variant="text"
							type="button"
							data-testid="replacementReason-backButton"
							onClick={handleBack}
						>
							{BackText.toUpperCase()}
						</Button>
						<Button
							type="submit"
							fullWidth
							variant="contained"
							data-testid="replacementReason-continueButton"
							data-id="replacementReason-continueButton"
						>
							{ContinueText}
						</Button>
					</ReplacementReasonButtonGroup>
				</ReplacementReasonContainer>
			</form>
		</FormProvider>
	);
};

export default ReplacementReason;
